import { useEffect, useState } from "react";

import { Callout, Colors, Icon, Intent } from "@blueprintjs/core";
import styled from "@emotion/styled";
import capitalize from "lodash/capitalize";
import { Link, useParams } from "react-router";

import { Tooltip } from "@/components/helpers/ui/blueprint-overrides/Tooltip";
import { isProgramLead } from "@/components/helpers/workspace/permissions";
import { Box, Flex } from "@/components/layout/flexbox";
import { Nav } from "@/components/nav/Nav";
import { buildComponentMetadata } from "@/components/pages_logged_in/program/components/componentMetadata";
import { ProgramDetailsLoader } from "@/components/pages_logged_in/program/components/ProgramDetailsLoader";
import { ProgramRoleplayResponse } from "@/components/pages_logged_in/program/components/ProgramRoleplayResponse";
import { ProgramComponentStatus } from "@/components/pages_logged_in/program/components/program-component/ProgramComponentStatus";
import { ActionItemDisplay } from "@/components/pages_logged_in/program/components/set-components/ActionItemDisplay";
import { decodeHtmlEntities } from "@/components/pages_logged_in/roleplays/details/scenario/Notes";
import { Avatar } from "@/components/pieces/users/Avatar";
import { CARD_CLASSES } from "@/css/constants";
import { useProgramComponentResponseQueryLazyQuery } from "@/graphql";
import { SurveyAnswer as SurveyAnswerType } from "@/types/surveys";
import { ensureNonNullArray } from "@/utils/array";
import { formatDate } from "@/utils/datetime";

import { ProgramCoachingResponse } from "./ProgramCoachingResponse";
import { ONE_ONE_COACHING_STAGE_STYLES } from "./utils";

const ratingScaleOptions = [
  {
    id: "1",
    backgroundColor: "#C61D1D",
    borderColor: "#C61D1D",
  },
  {
    id: "2",
    backgroundColor: "#F5E6E8",
    borderColor: "#DD8183",
  },
  {
    id: "3",
    backgroundColor: "#F0F0F0",
    borderColor: "#A6A6A6",
  },
  {
    id: "4",
    backgroundColor: "#E0F0E7",
    borderColor: "#79C699",
  },
  {
    id: "5",
    backgroundColor: "#129B4C",
    borderColor: "#129B4C",
  },
];

interface SurveyAnswerProps {
  formFieldAnswer: SurveyAnswerType & { formField?: { prompt: string } | null };
}

function SurveyAnswer({ formFieldAnswer }: SurveyAnswerProps) {
  const question = formFieldAnswer.formField?.prompt;
  const answerValue = formFieldAnswer.value;
  const answerOptionValue = formFieldAnswer.optionValue?.label;

  const answerOption =
    formFieldAnswer.optionValue?.value &&
    (ratingScaleOptions.find(
      (item) => item.id === formFieldAnswer.optionValue?.value,
    ) || { backgroundColor: "#F0F0F0", borderColor: "#A6A6A6" });

  return (
    <Box
      p={24}
      className={CARD_CLASSES}
      css={{ background: Colors.WHITE }}
      mt={32}
    >
      <Box fontSize={18} fontWeight={500} mb={24}>
        {question}
      </Box>
      <Flex alignItems="center">
        {answerOption && (
          <Box
            width={12}
            height={12}
            style={{
              backgroundColor: answerOption.backgroundColor,
              border: `1px solid ${answerOption.borderColor}`,
              borderRadius: "4px",
            }}
            mr={2}
          ></Box>
        )}
        {decodeHtmlEntities(answerValue || answerOptionValue || "")}
      </Flex>
    </Box>
  );
}

export function ProgramComponentResponse() {
  const [errorMessage, setErrorMessage] = useState("");
  const { responseId, id } = useParams();
  const isSurveyPath = window.location.pathname.includes("surveys");

  const [fetch, { data, loading }] = useProgramComponentResponseQueryLazyQuery({
    onCompleted: (data) => {
      if (!data?.programComponentResponse) {
        setErrorMessage("Something went wrong. Please try again.");
      }
    },
    fetchPolicy: "network-only",
    onError: (err) => {
      setErrorMessage(err.message);
    },
  });
  useEffect(() => {
    if (responseId) {
      fetch({ variables: { responseId } });
    }
  }, [responseId]);

  const { programComponentResponse } = data || {};

  const {
    user,
    programSurvey,
    completedAt,
    programComponent,
    roleplayScenarios = [],
  } = programComponentResponse || {};

  const formFieldAnswers = ensureNonNullArray(
    programComponentResponse?.formFieldAnswers,
  );

  const componentStatus = programComponent?.status;
  const formFields = ensureNonNullArray(programSurvey?.formFields);

  const statusView = programComponent ? (
    <ProgramComponentStatus component={programComponent} />
  ) : null;

  const isLead = isProgramLead(id, false);

  if (loading && !data) {
    return (
      <ProgramDetailsLoader
        includeSidebar={true}
        hasHeader={false}
        sizeNavWidth={0}
      />
    );
  }

  if (errorMessage || !programComponent || !id) {
    return (
      <Box mt={36}>
        <Nav />
        <Callout title="Oops, we ran into an issue" intent={Intent.WARNING}>
          {errorMessage}
        </Callout>
      </Box>
    );
  }

  const metaData = buildComponentMetadata({
    component: programComponent,
  });

  const { name: userName = "Unknown User" } = user || {};
  const surveyName = programComponent?.name;

  return (
    <Box>
      <Nav />
      <Box flex={1} mt={24}>
        <Flex alignItems={"center"}>
          <Box flex={1}>
            <StyledLink
              to={`/programs/${id}/${
                isSurveyPath ? "surveys" : "action-items"
              }/${programComponent?.id}`}
            >
              <Box fontSize={24} fontWeight={600}>
                {surveyName}
              </Box>
              {statusView}
            </StyledLink>
          </Box>
        </Flex>
      </Box>
      <Box className="bbs" my={24} />
      <Flex alignItems="center">
        {user && (
          <Tooltip content={user.email}>
            <Avatar size={48} user={user} />
          </Tooltip>
        )}
        <Flex flex={1} flexDirection={["column", "row"]} ml={3}>
          <Box flex={1} fontSize={18} fontWeight={600}>
            {userName}
          </Box>
          {completedAt && !metaData.isOneOneMaster && (
            <Box mt={[1, 0]}>
              {capitalize(metaData.componentTypeName)} completed on{" "}
              {formatDate(completedAt, "MMM d, yyyy, hh:mm a")}
            </Box>
          )}
          {metaData.isOneOneMaster && (
            <Box mt={[1, 0]}>
              <Flex alignItems="center">
                <Icon
                  icon="circle"
                  size={14}
                  color={
                    ONE_ONE_COACHING_STAGE_STYLES["SessionActive"].textColor
                  }
                />
                <Box
                  fontSize={14}
                  fontWeight={500}
                  ml="5px"
                  color={
                    ONE_ONE_COACHING_STAGE_STYLES["SessionActive"].textColor
                  }
                >
                  {"Active Session"}
                </Box>
              </Flex>
            </Box>
          )}
        </Flex>
      </Flex>

      {!metaData.isOneOneMaster && (
        <Box
          flex={1}
          px={32}
          py={32}
          my={24}
          className={CARD_CLASSES}
          css={{ background: Colors.WHITE }}
        >
          {metaData.isSurvey && (
            <>
              <Box fontSize={18} fontWeight={600}>
                Responses
              </Box>
              {formFieldAnswers.length > 0
                ? formFieldAnswers.map((answer) => {
                    return (
                      <SurveyAnswer
                        key={answer.id}
                        formFieldAnswer={answer ?? undefined}
                      />
                    );
                  })
                : "No responses yet"}
            </>
          )}

          {metaData.isActionItems &&
            formFields.map((question, index) => {
              const answer = formFieldAnswers.find(
                (item) => item.formField?.id === question?.id,
              );

              if (!answer) return null;

              const isComplete = !!answer.value || !!answer.userFile;

              return (
                <Box key={question?.id} mt={index > 0 ? 3 : 0}>
                  <ActionItemDisplay
                    isLead={false}
                    isComplete={isComplete}
                    question={question}
                    readOnly={true}
                    answer={answer}
                    componentStatus={componentStatus}
                  />
                </Box>
              );
            })}

          {metaData.isAIRoleplay && (
            <ProgramRoleplayResponse
              roleplayScenarios={ensureNonNullArray(roleplayScenarios)}
              isLead={isLead}
            />
          )}
        </Box>
      )}
      {metaData.isOneOneMaster && user?.id && (
        <ProgramCoachingResponse
          component={programComponent}
          participantId={user.id}
        />
      )}
    </Box>
  );
}

export const StyledLink = styled(Link)`
  color: ${Colors.BLACK};
  &:hover {
    opacity: 0.8;
    color: ${Colors.BLACK};
  }
`;
